import { addDays, format, isBefore, isSameDay, isWeekend, startOfDay } from 'date-fns';

export const PUBLIC_HOLIDAYS = [
  { date: '2026-05-05', label: '어린이날' },
  { date: '2026-05-25', label: '부처님오신날 대체공휴일' },

  { date: '2026-06-03', label: '전국동시지방선거' },

  { date: '2026-07-17', label: '제헌절' },

  { date: '2026-08-17', label: '광복절 대체공휴일' },

  { date: '2026-09-24', label: '추석 연휴' },
  { date: '2026-09-25', label: '추석' },

  { date: '2026-10-05', label: '개천절 대체공휴일' },
  { date: '2026-10-09', label: '한글날' },

  { date: '2026-12-25', label: '성탄절' },
];

export const PUBLIC_HOLIDAY_DATE_KEYS = new Set(PUBLIC_HOLIDAYS.map((h) => h.date));

/**
 * 요일·시간(오전/오후)별 시작일이 "오늘 이후 N번째 선택 가능일(평일·비공휴일)"인지를 나타내는 N.
 * 주말·공휴일은 세지 않고 건너뛴다. 예) 금요일 오후 = 2 → 오늘 이후 두 번째 선택 가능일.
 * - 인덱스: 0(일)~6(토), getDay() 반환값과 동일.
 * - am: 정오(12시) 미만, pm: 정오 이상.
 */
const STIMULATION_START_NTH: Record<number, { am: number; pm: number }> = {
  0: { am: 2, pm: 2 }, // 일
  1: { am: 1, pm: 2 }, // 월
  2: { am: 1, pm: 2 }, // 화
  3: { am: 1, pm: 2 }, // 수
  4: { am: 1, pm: 2 }, // 목
  5: { am: 1, pm: 2 }, // 금
  6: { am: 2, pm: 2 }, // 토
};

/** 평일이면서 공휴일이 아닌(= 선택 가능한) 날인지 */
export function isSelectableDay(date: Date): boolean {
  return !isWeekend(date) && !PUBLIC_HOLIDAY_DATE_KEYS.has(format(date, 'yyyy-MM-dd'));
}

/** 오늘 다음 날부터 세어 n번째 선택 가능한(평일·비공휴일) 날의 시작 시각 */
export function getNthSelectableDayAfterToday(now: Date, n: number): Date {
  let d = addDays(startOfDay(now), 1);
  let count = 0;
  while (count < n) {
    if (isSelectableDay(d)) {
      count += 1;
      if (count === n) {
        break;
      }
    }
    d = addDays(d, 1);
  }
  return startOfDay(d);
}

/**
 * 시작일(`disallowSelectToday`) 기준 가장 이른 선택 가능일(= 최소 시작일 하한선).
 * 이 날짜 이후의 주말·공휴일은 선택 가능하며, 이 함수는 하한선만 정한다.
 * N번째 선택 가능일은 주말·공휴일을 건너뛰며 센다.
 * - 오늘이 공휴일: 오늘 이후 두 번째 선택 가능일(= 다음 평일 + 1일)
 * - 그 외: 요일·오전/오후별 차수(STIMULATION_START_NTH)번째 선택 가능일
 */
export function getEarliestStimulationStartDate(now: Date): Date {
  // 오늘이 공휴일이면 두 번째 선택 가능일
  if (PUBLIC_HOLIDAY_DATE_KEYS.has(format(now, 'yyyy-MM-dd'))) {
    return getNthSelectableDayAfterToday(now, 2);
  }

  const nth = STIMULATION_START_NTH[now.getDay()];
  const n = now.getHours() >= 12 ? nth.pm : nth.am;
  return getNthSelectableDayAfterToday(now, n);
}

/**
 * disallowToday: 오늘 선택 불가 + getEarliestStimulationStartDate 이전 날짜 선택 불가
 * dev·qa 환경에서는 테스트 편의를 위해 제약 없이 모든 날짜를 허용한다.
 */
export function isStimulationCalendarSelectableDate(date: Date, options?: { disallowToday?: boolean }): boolean {
  const env = import.meta.env.VITE_ENV;
  if (env === 'dev' || env === 'qa') {
    return true;
  }

  if (options?.disallowToday) {
    const now = new Date();
    if (isSameDay(date, now)) {
      return false;
    }
    const earliest = getEarliestStimulationStartDate(now);
    if (isBefore(startOfDay(date), earliest)) {
      return false;
    }
  }

  return true;
}
